import React, { useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import MuiAppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import CssBaseline from "@mui/material/CssBaseline";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import logo from "../assets/logo.png";
import { Popover, Transition } from "@headlessui/react";
import { useLocation, useNavigate } from "react-router-dom";
import { IoIosArrowDown } from "react-icons/io";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import PeopleIcon from "@mui/icons-material/People";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import { IoPersonAdd } from "react-icons/io5";
import { FaRegUser } from "react-icons/fa";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import toast from "react-hot-toast";
import Cookies from "js-cookie";

/* ─── MUI styled — dynamic widths only ──────────────────────────────── */
const drawerWidth = 240;

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  position: "fixed !important",
  top: 8,
  right: 8,
  zIndex: theme.zIndex.drawer + 1,
  width: open
    ? `calc(100% - ${drawerWidth}px - 16px)`
    : `calc(100% - ${theme.spacing(8)} - 1px - 16px)`,
  marginLeft: open ? drawerWidth : `calc(${theme.spacing(8)} + 1px)`,
  background: "transparent !important",
  boxShadow: "none !important",
  transition: theme.transitions.create(["width", "margin-left"], {
    easing: theme.transitions.easing.sharp,
    duration: open
      ? theme.transitions.duration.enteringScreen
      : theme.transitions.duration.leavingScreen,
  }),
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

/* ─── nav items ──────────────────────────────────────────────────────── */
const navItems = [
  {
    label: "Patients",
    icon: PeopleIcon,
    path: "/receptionist",
    activePaths: ["/receptionist", "/receptionist/add"],
    activeBg: "bg-orange-400",
    activeBar: "#fb923c",
    glow: "shadow-[0_4px_14px_rgba(251,146,60,0.45)]",
  },
  {
    label: "Checkup",
    icon: MedicalServicesIcon,
    path: "/checkup",
    activePaths: ["/checkup", "/checkup/add"],
    activeBg: "bg-orange-400",
    activeBar: "#fb923c",
    glow: "shadow-[0_4px_14px_rgba(253,186,116,0.45)]",
  },
  // {
  //   label: "Admission",
  //   icon: IoPersonAdd,
  //   path: "/admission",
  //   activePaths: ["/admission", "/admission/add"],
  //   activeBg: "bg-orange-400",
  //   activeBar: "#fb923c",
  //   glow: "shadow-[0_4px_14px_rgba(253,186,116,0.45)]",
  // },
];

/* ══════════════════════════════════════════════════════════════════════ */
export default function PortalLayout({ children }) {
  const [open, setOpen]                   = useState(true);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [loggedUser, setLoggedUser]       = useState({ id: "", name: "" });

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const id   = localStorage.getItem("user_id");
    const name = localStorage.getItem("user_name");
    if (id && name) setLoggedUser({ id, name });
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("user");
    Cookies.remove("XIOQUNVU1RPTUVSLUFVVEhFTlRJQ0FUSU9OIMSLQ1JFVC1LRVk=");
    toast.success("Logout Successfully");
    navigate("/login", { replace: true });
  };

  if (location.pathname === "/login") return children;

  const pageTitle = location.pathname.startsWith("/receptionist")
    ? "Patients"
    : location.pathname.startsWith("/checkup")
    ? "Checkup"
    : "Admission";
    

  return (
    <div className="flex items-stretch min-h-screen bg-slate-100">
      {/* Google Font */}
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap'); * { font-family: 'Plus Jakarta Sans', sans-serif; }`}</style>

      <CssBaseline />

      {/* ── TOP BAR ───────────────────────────────────────────────────── */}
      <AppBar open={open}>
        <div className="rounded-2xl border border-white/70 bg-white/85 backdrop-blur-xl shadow-[0_4px_24px_rgba(0,0,0,0.08)]">
          <Toolbar className="!min-h-[56px] !px-3">

            {/* Menu toggle */}
            <IconButton
              onClick={() => setOpen(!open)}
              edge="start"
              className="!mr-2 !w-9 !h-9 !rounded-xl !bg-gray-700/[0.07] hover:!bg-gray-700/[0.14] !transition-colors"
            >
              <MenuIcon className="!text-[20px] !text-gray-700" />
            </IconButton>

            {/* Page title */}
            <div className="ml-2">
              <p className="text-[15px] font-bold text-gray-700 m-0 leading-tight">{pageTitle}</p>
              <p className="text-[11px] text-gray-400 m-0">
                {new Date().toLocaleDateString("en-US", {
                  weekday: "long",
                  month: "short",
                  day: "numeric",
                })}
              </p>
            </div>

            {/* Right controls */}
            <div className="ml-auto flex items-center gap-3">
             
              {/* User chip + dropdown */}
              <Popover className="relative">
                <Popover.Button
                  as="div"
                  onClick={() => setIsPopoverOpen(!isPopoverOpen)}
                  className="flex items-center gap-2 px-3 py-[6px] rounded-full cursor-pointer select-none
                     bg-gradient-to-r from-gray-700  to-gray-500
                    shadow-[0_2px_12px_rgba(251,146,60,0.25)]
                    hover:shadow-[0_4px_20px_rgba(251,146,60,0.38)] transition-shadow duration-200"
                >
                  <AccountCircleIcon className="!text-[20px] !text-white/90" />
                  <span className="text-[13px] font-semibold text-white max-w-[120px] overflow-hidden text-ellipsis whitespace-nowrap">
                    {loggedUser.name || "Admin"}
                  </span>
                  <IoIosArrowDown
                    className="w-3.5 h-3.5 text-white/70 transition-transform duration-300"
                    style={{ transform: isPopoverOpen ? "rotate(-180deg)" : "rotate(0deg)" }}
                  />
                </Popover.Button>

                <Transition
                  as={React.Fragment}
                  enter="transition ease-out duration-150"
                  enterFrom="opacity-0 -translate-y-2"
                  enterTo="opacity-100 translate-y-0"
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100 translate-y-0"
                  leaveTo="opacity-0 -translate-y-2"
                >
                  <Popover.Panel
                    className="absolute top-[calc(100%+10px)] right-0 z-[9999]
                      min-w-[220px] rounded-2xl overflow-hidden
                      bg-white/[0.97] backdrop-blur-xl
                      border border-gray-700/10
                      shadow-[0_20px_60px_rgba(0,0,0,0.15)]"
                  >
                    {/* Header */}
                    <div className="flex items-center gap-2.5 p-4 bg-gradient-to-r from-gray-700  to-gray-500">
                      <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                        <FaRegUser className="text-white text-base" />
                      </div>
                      <div>
                        <p className="text-white text-[13px] font-bold m-0">{loggedUser.name || "Admin"}</p>
                        <p className="text-white/60 text-[11px] m-0">Receptionist</p>
                      </div>
                    </div>

                    {/* Logout */}
                    <div
                      onClick={handleLogout}
                      className="flex items-center gap-2.5 m-2.5 px-3.5 py-2.5 rounded-xl cursor-pointer
                        bg-gradient-to-r from-red-500 to-red-600
                        text-white font-semibold text-[13px]
                        shadow-[0_3px_12px_rgba(239,68,68,0.3)]
                        hover:-translate-y-px hover:shadow-[0_6px_18px_rgba(239,68,68,0.4)]
                        transition-all duration-200"
                    >
                      <PowerSettingsNewIcon className="!text-[18px]" />
                      <span>Sign Out</span>
                    </div>
                  </Popover.Panel>
                </Transition>
              </Popover>
            </div>
          </Toolbar>
        </div>
      </AppBar>

      {/* ── SIDEBAR ───────────────────────────────────────────────────── */}
      <Drawer
        variant="permanent"
        open={open}
        sx={{
        "& .MuiDrawer-paper": {
          border: "none",
          borderTopRightRadius: 24,
          borderBottomRightRadius: 24,
          background:"linear-gradient(160deg, #1e1b2e 0%, #1e1b2e 50%, #2d3a4a 90%, #374151 100%)",
          boxShadow: "6px 0 32px rgba(0,0,0,0.22)",
          overflow: "hidden",
          zIndex: 40,   
        },

        }}
      >
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
                    "radial-gradient(ellipse at 20% 15%, rgba(156,163,175,0.18) 0%, transparent 55%), radial-gradient(ellipse at 75% 85%, rgba(107,114,128,0.22) 0%, transparent 55%)",
          }}
        />

        {/* Logo */}
        <div className="relative flex items-center justify-center px-3 pt-5 pb-4 border-b border-white/[0.08] mb-2">
          <img
            src={logo}
            alt="logo"
            className="object-contain transition-all duration-300"
            style={{ width: open ? 80 : 32 }}
          />
        </div>

        {/* Section label */}
        {open && (
          <p className="relative text-[10px] font-bold tracking-[1.2px] uppercase text-white/30 px-5 pt-2 pb-1 m-0">
            Navigation
          </p>
        )}

        {/* Nav */}
        <nav className="relative py-1">
          {navItems.map((item) => {
            const isActive = item.activePaths.includes(location.pathname);
            const Icon = item.icon;
            return (
              <div
                key={item.path}
                onClick={() => navigate(item.path)}
                title={!open ? item.label : ""}
                className={[
                  "relative flex items-center cursor-pointer rounded-xl overflow-hidden",
                  "mx-2 my-1 px-3 py-2.5",
                  "transition-all duration-[250ms] ease-[cubic-bezier(.4,0,.2,1)]",
                  isActive
                    ? "text-white bg-white/[0.12] shadow-[0_4px_20px_rgba(0,0,0,0.2),inset_0_0_0_1px_rgba(255,255,255,0.15)]"
                    : "text-white/60 hover:text-white/95 hover:bg-white/[0.08]",
                ].join(" ")}
              >
                {/* Icon wrap */}
                <div
                  className={[
                    "flex items-center justify-center w-[34px] h-[34px] rounded-[9px] flex-shrink-0 transition-all duration-[250ms]",
                    isActive
                      ? `${item.activeBg} ${item.glow}`
                      : "bg-white/10 hover:bg-white/[0.16]",
                  ].join(" ")}
                >
                  <Icon sx={{ fontSize: 18, color: isActive ? "#fff" : "rgba(255,255,255,0.75)" }} />
                </div>

                {/* Label */}
                <span
                  className={[
                    "ml-3 text-[13.5px] font-semibold whitespace-nowrap overflow-hidden transition-all duration-300",
                    open ? "max-w-[160px] opacity-100" : "max-w-0 opacity-0",
                  ].join(" ")}
                >
                  {item.label}
                </span>

                {/* Active bar */}
                {isActive && (
                  <div
                    className="absolute right-0 top-1/2 -translate-y-1/2 w-[3px] h-[60%] rounded-l"
                    style={{ background: item.activeBar }}
                  />
                )}
              </div>
            );
          })}
        </nav>
      </Drawer>

      {/* ── MAIN CONTENT ──────────────────────────────────────────────── */}
      <Box component="main" sx={{ flexGrow: 1, p: 0 }}>
        <DrawerHeader />
        <div className="bg-white min-h-[calc(100vh-80px)] p-4 mr-2 mb-2 rounded-2xl shadow-[0_2px_16px_rgba(0,0,0,0.06)]">
          {children}
        </div>
      </Box>
    </div>
  );
}